import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  CircularProgress,


} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import { useTrainingEnrollments } from '../../api/hooks/useTrainingEnrollments';
import { useTrainingStructure } from '../../api/hooks/useTrainingStructure';
import { useTrainingProgressByEnrollment } from '../../api/hooks/useTrainingProgress';
import { useOrganization } from '../../contexts/OrganizationContext';
import {
  TrainingEnrollmentDto,
  TrainingModuleDto,
  TrainingTopicDto,
  TrainingContentDto,
  ContentProgressItemDto,
} from '../../api/types/dtos/training';
import styles from './TrainingProgressView.module.scss';

interface TrainingProgressViewProps {
  organizationId: string; // The farm organization ID
}

interface ProcessedSection {
  module: TrainingModuleDto;
  topics: Array<{
    topic: TrainingTopicDto;
    isCompleted: boolean;
    contents: TrainingContentDto[];
  }>;
  progressPercentage: number;
}

/**
 * Check if all contents in a topic are completed
 */
const isTopicCompleted = (
  topic: TrainingTopicDto,
  contents: TrainingContentDto[],
  contentProgress: ContentProgressItemDto[]
): boolean => {
  const topicContents = contents.filter((c) => c.topicId === topic.id);
  if (topicContents.length === 0) return false;

  const progressMap = new Map(
    contentProgress.map((cp) => [cp.contentId, cp.isCompleted])
  );

  return topicContents.every((content) => {
    const progress = progressMap.get(content.id);
    // If no progress record exists, treat as incomplete
    return progress === true;
  });
};

/**
 * Calculate progress percentage for a section/module
 */
const calculateSectionProgress = (
  module: TrainingModuleDto,
  topics: TrainingTopicDto[],
  contents: TrainingContentDto[],
  contentProgress: ContentProgressItemDto[]
): number => {
  const moduleTopics = topics.filter((t) => t.moduleId === module.id);
  
  let totalContribution = 0;
  moduleTopics.forEach((topic) => {
    if (isTopicCompleted(topic, contents, contentProgress)) {
      totalContribution += topic.progressContribution;
    }
  });

  // Cap at 100%
  return Math.min(Math.round(totalContribution), 100);
};

export const TrainingProgressView: React.FC<TrainingProgressViewProps> = ({
  organizationId,
}) => {
  const { selectedOrganization } = useOrganization();
  const parentOrgId = selectedOrganization?.id || '';

  // Fetch enrollments for the farm organization
  const { data: enrollments = [], isLoading: isLoadingEnrollments } =
    useTrainingEnrollments(organizationId);

  return (
    <Box className={styles.container}>
      {isLoadingEnrollments ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : !parentOrgId ? (
        <Box className={styles.cardGroup}>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>
              Desarrollo de Capacidades
            </Typography>
          </Box>
          <Box className={styles.levelSection}>
            <Typography variant="body1" color="textSecondary">
              No se pudo cargar la información de capacitaciones.
            </Typography>
          </Box>
        </Box>
      ) : enrollments.length === 0 ? (
        <Box className={styles.cardGroup}>
          <Box className={styles.sectionHeader}>
            <Typography className={styles.sectionTitle}>
              Desarrollo de Capacidades
            </Typography>
          </Box>
          <Box className={styles.levelSection}>
            <Typography variant="body1" color="textSecondary">
              No hay capacitaciones inscritas para esta organización.
            </Typography>
          </Box>
        </Box>
      ) : (
        <TrainingSectionsList
          organizationId={organizationId}
          parentOrgId={parentOrgId}
          enrollments={enrollments}
        />
      )}
    </Box>
  );
};

interface TrainingSectionsListProps {
  organizationId: string;
  parentOrgId: string;
  enrollments: TrainingEnrollmentDto[];
}

/**
 * Component that processes and displays sections for all enrollments
 */
const TrainingSectionsList: React.FC<TrainingSectionsListProps> = ({
  organizationId,
  parentOrgId,
  enrollments,
}) => {
  // Group enrollments by training
  const trainingMap = useMemo(() => {
    const map = new Map<string, TrainingEnrollmentDto[]>();
    enrollments.forEach((enrollment) => {
      const existing = map.get(enrollment.trainingId) || [];
      existing.push(enrollment);
      map.set(enrollment.trainingId, existing);
    });
    return map;
  }, [enrollments]);

  return (
    <Box className={styles.cardGroup}>
      {/* Section Header */}
      <Box className={styles.sectionHeader}>
        <Typography className={styles.sectionTitle}>
          Desarrollo de Capacidades
        </Typography>
      </Box>
      
      {/* Individual trainings */}
      {Array.from(trainingMap.entries()).map(([trainingId, trainingEnrollments], index, array) => {
        const isLastTraining = index === array.length - 1;
        return (
          <TrainingSectionsForEnrollment
            key={trainingId}
            organizationId={organizationId}
            parentOrgId={parentOrgId}
            trainingId={trainingId}
            enrollment={trainingEnrollments[0]} // Use first enrollment for structure
            isFirstTraining={index === 0}
            isLastTraining={isLastTraining}
          />
        );
      })}
    </Box>
  );
};

interface TrainingSectionsForEnrollmentProps {
  organizationId: string;
  parentOrgId: string;
  trainingId: string;
  enrollment: TrainingEnrollmentDto;
  isFirstTraining: boolean;
  isLastTraining: boolean;
}

/**
 * Component that displays sections for a single training enrollment
 */
const TrainingSectionsForEnrollment: React.FC<TrainingSectionsForEnrollmentProps> = ({
  organizationId,
  parentOrgId,
  trainingId,
  enrollment,


}) => {
  // Fetch training structure (requires parent org ID)
  const {
    data: trainingStructure,
    isLoading: isLoadingStructure,
  } = useTrainingStructure(parentOrgId, trainingId);

  // Fetch progress for this enrollment
  const {
    data: progressData = [],
    isLoading: isLoadingProgress,
  } = useTrainingProgressByEnrollment(organizationId, enrollment.id);

  const isLoading = isLoadingStructure || isLoadingProgress;

  // Process the data
  const sections = useMemo((): ProcessedSection[] => {
    if (!trainingStructure || !parentOrgId) return [];

    const { modules, topics, contents } = trainingStructure;
    
    // Create content progress array from progress data
    // TrainingProgressDto has id field, ContentProgressItemDto uses progressId
    const contentProgressArray: ContentProgressItemDto[] = progressData.map((p) => ({
      progressId: p.id,
      contentId: p.contentId,
      isCompleted: p.isCompleted,
    }));

    // Sort modules by sequence
    const sortedModules = [...modules].sort((a, b) => a.sequence - b.sequence);

    return sortedModules.map((module) => {
      // Get topics for this module, sorted by sequence
      const moduleTopics = topics
        .filter((t) => t.moduleId === module.id)
        .sort((a, b) => a.sequence - b.sequence)
        .map((topic) => {
          const topicContents = contents.filter((c) => c.topicId === topic.id);
          const isCompleted = isTopicCompleted(topic, contents, contentProgressArray);

          return {
            topic,
            isCompleted,
            contents: topicContents,
          };
        });

      const progressPercentage = calculateSectionProgress(
        module,
        topics,
        contents,
        contentProgressArray
      );

      return {
        module,
        topics: moduleTopics,
        progressPercentage,
      };
    });
  }, [trainingStructure, progressData]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={100} py={3}>
        <CircularProgress size={32} />
      </Box>
    );
  }

  if (sections.length === 0) {
    return null;
  }

  // Get training name from first module (or use a default)
  const trainingName = sections[0]?.module?.name || 'Capacitación';

  return (
    <Box className={styles.levelSection}>
      {/* Training name as subtitle */}
      <Typography className={styles.levelTitle}>
        {trainingName}
      </Typography>
      
      {/* Modules within this training */}
      {sections.map((section, index) => {

        return (
          <Box key={section.module.id} className={styles.moduleCard}>
            <Box className={styles.sectionContent}>
              {/* Left: Module (Icon and Title) */}
              <Box className={styles.leftSection}>
                <Box className={styles.iconContainer}>
                  {section.module.logo ? (
                    <img
                      src={section.module.logo}
                      alt={section.module.name}
                      className={styles.iconImage}
                    />
                  ) : (
                    <SchoolIcon className={styles.iconPlaceholder} />
                  )}
                </Box>
                <Typography variant="body1" className={styles.moduleTitle}>
                  {section.module.name}
                </Typography>
              </Box>

              {/* Right: Topics list and Progress badge */}
              <Box className={styles.rightSection}>
                <Box className={styles.topicsList}>
                  {section.topics.map(({ topic, isCompleted }) => (
                    <Box key={topic.id} className={styles.topicItem}>
                      <Box className={isCompleted ? styles.checkboxCompleted : styles.checkboxIncomplete}>
                        {isCompleted && <StarIcon className={styles.starIcon} />}
                      </Box>
                      <Typography variant="body2" className={styles.topicName}>
                        {topic.name}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                {/* Progress percentage badge */}
                <Box className={styles.progressBadge}>
                  <Typography 
                    variant="h4" 
                    className={styles.progressText}
                    sx={{ fontWeight: 700, fontSize: '24px' }}
                  >
                    {section.progressPercentage}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default TrainingProgressView;
